/**
 * Hook for Admin Hub command palette state and search
 */
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useAdminHub } from '../components/admin-hub/context/AdminHubContext';
import { getAllNavigablePages, type TreeNavItem } from '../config/adminHubConfig';

interface SearchResult {
  id: string;
  label: string;
  path: string;
  section: string;
  type: 'navigation' | 'recent' | 'favorite' | 'action';
  score: number;
}

interface UseAdminHubCommandPaletteReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: SearchResult[];
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  selectNext: () => void;
  selectPrevious: () => void;
  executeSelected: () => void;
  executeResult: (result: SearchResult) => void;
}

/**
 * Simple fuzzy matching score (0-1)
 */
const fuzzyMatch = (query: string, text: string): number => {
  if (!query) return 0;
  const lowerQuery = query.toLowerCase();
  const lowerText = text.toLowerCase();

  // Exact match
  if (lowerText === lowerQuery) return 1;

  // Starts with
  if (lowerText.startsWith(lowerQuery)) return 0.9;

  // Contains
  if (lowerText.includes(lowerQuery)) return 0.7;

  // Fuzzy match - check if all query characters appear in order
  let queryIndex = 0;
  let matchScore = 0;
  for (let i = 0; i < lowerText.length && queryIndex < lowerQuery.length; i++) {
    if (lowerText[i] === lowerQuery[queryIndex]) {
      matchScore += 1;
      queryIndex++;
    }
  }

  if (queryIndex === lowerQuery.length) {
    return (matchScore / lowerText.length) * 0.5;
  }

  return 0;
};

/**
 * Hook for command palette functionality
 */
export const useAdminHubCommandPalette = (): UseAdminHubCommandPaletteReturn => {
  const {
    commandPaletteOpen,
    openCommandPalette,
    closeCommandPalette,
    filteredNavTree,
    recentItems,
    favorites,
    navigateTo,
  } = useAdminHub();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Get all navigable pages
  const allPages = useMemo(
    () => getAllNavigablePages(filteredNavTree),
    [filteredNavTree]
  );

  // Build search results
  const searchResults = useMemo((): SearchResult[] => {
    const results: SearchResult[] = [];

    // If no query, show recent items first, then favorites
    if (!searchQuery.trim()) {
      // Add recent items
      recentItems.slice(0, 5).forEach((item, idx) => {
        results.push({
          id: `recent-${item.path}`,
          label: item.label,
          path: item.path,
          section: item.section,
          type: 'recent',
          score: 1 - (idx * 0.1),
        });
      });

      // Add favorites that aren't in recents
      favorites.forEach((fav, idx) => {
        if (!recentItems.some(r => r.path === fav.path)) {
          results.push({
            id: `favorite-${fav.path}`,
            label: fav.label,
            path: fav.path,
            section: fav.section,
            type: 'favorite',
            score: 0.5 - (idx * 0.05),
          });
        }
      });

      return results;
    }

    // Search through all pages
    const scoredPages = allPages
      .map(page => {
        const labelScore = fuzzyMatch(searchQuery, page.label);
        const sectionScore = fuzzyMatch(searchQuery, page.section) * 0.5;
        const pathScore = fuzzyMatch(searchQuery, page.path) * 0.3;
        const score = Math.max(labelScore, sectionScore, pathScore);

        // Boost score for favorites and recents
        let boost = 0;
        if (favorites.some(f => f.path === page.path)) boost += 0.1;
        if (recentItems.some(r => r.path === page.path)) boost += 0.15;

        return {
          id: `nav-${page.id}`,
          label: page.label,
          path: page.path,
          section: page.section,
          type: 'navigation' as const,
          score: score + boost,
        };
      })
      .filter(result => result.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);

    return scoredPages;
  }, [searchQuery, allPages, recentItems, favorites]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchResults]);

  // Reset search when closing
  useEffect(() => {
    if (!commandPaletteOpen) {
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [commandPaletteOpen]);

  // Keyboard navigation
  const selectNext = useCallback(() => {
    setSelectedIndex(prev =>
      prev < searchResults.length - 1 ? prev + 1 : prev
    );
  }, [searchResults.length]);

  const selectPrevious = useCallback(() => {
    setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
  }, []);

  const executeResult = useCallback((result: SearchResult) => {
    navigateTo(result.path);
  }, [navigateTo]);

  const executeSelected = useCallback(() => {
    if (searchResults[selectedIndex]) {
      executeResult(searchResults[selectedIndex]);
    }
  }, [searchResults, selectedIndex, executeResult]);

  const toggle = useCallback(() => {
    if (commandPaletteOpen) {
      closeCommandPalette();
    } else {
      openCommandPalette();
    }
  }, [commandPaletteOpen, openCommandPalette, closeCommandPalette]);

  return {
    isOpen: commandPaletteOpen,
    open: openCommandPalette,
    close: closeCommandPalette,
    toggle,
    searchQuery,
    setSearchQuery,
    searchResults,
    selectedIndex,
    setSelectedIndex,
    selectNext,
    selectPrevious,
    executeSelected,
    executeResult,
  };
};

export default useAdminHubCommandPalette;
